/**
 * Legacy DynamicTable (V1) → ExternalTableSchema (V2) adapter.
 * Converts schema.columns / schema.options / props into a V2 external schema.
 */

import type { InvitationBlockData } from '../../../../types/common';
import type {
  AggregateDef,
  BadgeConfig,
  ColumnType,
  ComputeRule,
  DataSourceConfig,
  DataSourceDef,
  EditableDef,
  ExternalColumnDef,
  ExternalTableSchema,
  FeaturesConfig,
  FilterDef,
  FormatConfig,
  SubRowConfig,
  ValidationDef,
} from './types';

// ---------------------------------------------------------------------------
// Legacy props surface (V1 DynamicTable)
// ---------------------------------------------------------------------------

export interface LegacyDynamicTableProps {
  schema: {
    columns: any[];
    subRowColumns?: any[];
    options?: {
      showToolbar?: boolean;
      showPagination?: boolean;
      showFilter?: boolean;
      filterColumn?: number;
      filters?: any[];
      groupBy?: string[];
      showSearch?: boolean;
      showColumn?: boolean;
      paginationCount?: number;
      subRowKey?: string;
      enableSearch?: boolean;
      showSelectedFilter?: boolean;
      showImportBlock?: boolean;
      importDescription?: string;
    };
    computeRules?: Array<{ sourceFields: string[]; operator: string; targetField: string }>;
    apiInjection?: Array<{
      name: string;
      url: string;
      method?: string;
      injectDataToField: string;
      mapper: { label: string; value: string };
    }>;
    subRowApiInjection?: Array<{
      name: string;
      url: string;
      method?: string;
      injectDataToField: string;
      mapper: { label: string; value: string };
    }>;
    endpoint?: string;
    rowIdField?: string;
  };
  showCheckBox?: boolean;
  checkedValues?: string[];
  filteredKey?: string;
  isTableFor?: string;
  checkboxFor?: string;
  coloredTable?: boolean;
  scrollableHeight?: number;
  subRowKey?: string;
  groupBy?: string[];
  showPagination?: boolean;
  showColumn?: boolean;
  selectedColumnList?: string[];
  showSelectAllButton?: boolean;
  onLoadMore?: () => void;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// V2 callback bag (extracted from V1 root props)
// ---------------------------------------------------------------------------

export interface V2CallbackProps {
  onChecked?: (checkedIds: string[]) => void;
  onSave?: (editedData: unknown[]) => Promise<void>;
  handleExportBulk?: (value?: string) => void;
  handleSendInvite?: (value?: string) => void;
  handleColumnSave?: (value: string[]) => void;
  handleTableColumn?: (columnValue: string) => void;
  handleEarnMultiplier?: (columnValue: string) => void;
  handlePublishAward?: unknown;
  handleSendEmail?: unknown;
  onLoadMore?: () => void;
  onFilterClick?: (isResetClick?: boolean) => void;
  onCheckboxEditDataChange?: (data: Map<string, Record<string, unknown>>) => void;
  setIsHovering?: unknown;
  setSelectedTableData?: unknown;
  setCheckedItems?: unknown;
  setInviteStatus?: unknown;
  inivitationBlockDatas?: InvitationBlockData;
  components?: Record<string, () => JSX.Element>;
}

const COMPUTE_OPS = new Set<ComputeRule['operator']>(['add', 'subtract', 'multiply', 'divide']);

function normalizeHttpMethod(m?: string): 'GET' | 'POST' | undefined {
  if (!m) return undefined;
  const u = String(m).toUpperCase();
  return u === 'POST' ? 'POST' : 'GET';
}

function toInjectionDef(inj: {
  url: string;
  method?: string;
  mapper: { label: string; value: string };
}): DataSourceDef {
  return {
    url: inj.url,
    method: normalizeHttpMethod(inj.method),
    mapper: inj.mapper,
  };
}

function buildInjectionMap(
  list: LegacyDynamicTableProps['schema']['apiInjection'] | undefined,
): Map<string, DataSourceDef> {
  const map = new Map<string, DataSourceDef>();
  if (!Array.isArray(list)) return map;
  for (const inj of list) {
    if (inj?.injectDataToField) {
      map.set(String(inj.injectDataToField), toInjectionDef(inj));
    }
  }
  return map;
}

function mapV1FilterToV2(f: Record<string, unknown>): FilterDef {
  const raw = String(f.type ?? '');
  const allowed: FilterDef['type'][] = [
    'select',
    'asyncSelect',
    'date',
    'dateRange',
    'text',
    'number',
  ];
  let type: FilterDef['type'] = (allowed as string[]).includes(raw)
    ? (raw as FilterDef['type'])
    : 'text';
  if (!(allowed as string[]).includes(raw)) {
    if (typeof f.fetchFrom === 'string' && f.fetchFrom.length > 0) type = 'asyncSelect';
    else if (Array.isArray(f.enum) && f.enum.length > 0) type = 'select';
  }

  const base: FilterDef = {
    name: String(f.name ?? ''),
    placeholder: String(f.placeholder ?? ''),
    type,
  };
  const enumOpts = f.enum as Array<{ label?: string; value?: unknown }> | undefined;
  if (Array.isArray(enumOpts)) {
    base.options = enumOpts.map((o) => ({
      label: String(o.label ?? o.value ?? ''),
      value: o.value,
    }));
  }
  if (f.isMulti === true) base.multiple = true;
  if (Array.isArray(f.clears)) base.clears = f.clears.map(String);
  if (f.dependencyName != null) base.dependsOn = String(f.dependencyName);
  const rangeEnd =
    (f.rangeEndField as string | undefined) ??
    (f.rangeEndName as string | undefined) ??
    (f.endField as string | undefined);
  if (typeof rangeEnd === 'string' && rangeEnd.length > 0) {
    base.rangeEndField = rangeEnd;
  }
  if (typeof f.rangeEndPlaceholder === 'string') {
    base.rangeEndPlaceholder = f.rangeEndPlaceholder;
  }
  if (typeof f.minDate === 'string') base.minDate = f.minDate;
  if (typeof f.maxDate === 'string') base.maxDate = f.maxDate;
  const fetchFrom = f.fetchFrom as string | undefined;
  const mapper = f.mapper as Record<string, string> | undefined;
  if (fetchFrom) {
    base.dataSource = {
      url: fetchFrom,
      labelField: mapper?.label ?? 'label',
      valueField: mapper?.value ?? 'value',
    };
  }
  return base;
}

function mapColumnValidation(v: Record<string, unknown> | undefined): ValidationDef | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const t = v.type as string | undefined;
  const type: ValidationDef['type'] =
    t === 'number' || t === 'date' || t === 'boolean' ? t : 'string';
  const whenRaw = v.when as Array<Record<string, unknown>> | undefined;
  return {
    type,
    rules: v.rules as ValidationDef['rules'],
    when: whenRaw?.map((w) => ({
      dependsOn: w.deps as string | string[],
      is: w.is,
      then: w.then as ValidationDef['rules'],
      otherwise: w.otherwise as ValidationDef['rules'],
    })),
  };
}

function mapEditableType(t: string | undefined): EditableDef['type'] {
  if (!t) return 'text';
  if (t === 'dropdown') return 'select';
  const allowed: EditableDef['type'][] = [
    'text',
    'number',
    'date',
    'dateTime',
    'select',
    'asyncSelect',
    'dynamicSelect',
    'checkbox',
    'textarea',
    'checkboxDriven',
    'string',
  ];
  return (allowed as string[]).includes(t) ? (t as EditableDef['type']) : 'text';
}

/** V1 boolean flags that imply a legacy column shape (used only when columns are empty). */
const V1_COLUMN_FLAG_KEYS = [
  'isDateField',
  'isDateFieldWithTime',
  'isDateFieldWithTimeLTS',
  'isConditionalDateField',
  'isBadgeField',
  'isInjectedBadgeField',
  'isDefaultBadgeField',
  'isCurrencyField',
  'isNumberField',
  'isPercentageField',
  'isConditionalField',
  'isModalField',
  'isHtmlFormattedField',
  'isCoupleField',
  'isCoupleDateRangeField',
  'isLinkField',
  'isDateRangeField',
  'isMaskBehaviorField',
  'isTimeField',
  'isArraySpliterField',
  'isSpaceField',
] as const;

function firstColumnLooksV1(col: Record<string, unknown> | undefined): boolean {
  if (!col) return false;
  return col.Header != null || col.accessor != null;
}

function firstColumnLooksV2(col: Record<string, unknown> | undefined): boolean {
  if (!col) return false;
  return typeof col.field === 'string' && col.field.length > 0;
}

function columnHasV1TypeFlags(col: Record<string, unknown>): boolean {
  return V1_COLUMN_FLAG_KEYS.some((k) => col[k] === true);
}

function hasV1RootPropFlags(props: Record<string, unknown> | undefined): boolean {
  if (!props) return false;
  return (
    props.showCheckBox === true ||
    props.coloredTable === true ||
    props.scrollableHeight != null ||
    props.filteredKey != null ||
    props.checkboxFor != null ||
    props.showSelectAllButton === true ||
    props.subRowKey != null ||
    Array.isArray(props.groupBy)
  );
}

function hasV1OptionsFlags(opts: Record<string, unknown> | undefined): boolean {
  if (!opts || typeof opts !== 'object') return false;
  return (
    opts.enableSearch === true ||
    opts.showImportBlock === true ||
    opts.paginationCount != null ||
    opts.subRowKey != null ||
    opts.showSelectedFilter === true
  );
}

/**
 * Detect whether input is authored as V1 (Header/accessor) or V2 (`field`).
 */
export function detectSchemaVersion(external: any, legacyProps?: any): '1.0' | '2.0' {
  // Multi-section schemas (sections[]) are always V2
  if (Array.isArray(external?.sections) && external.sections.length > 0) return '2.0';

  const cols = external?.columns;
  const first = Array.isArray(cols) && cols.length > 0 ? cols[0] : undefined;
  if (firstColumnLooksV1(first)) return '1.0';
  if (firstColumnLooksV2(first)) return '2.0';
  if (first && columnHasV1TypeFlags(first as Record<string, unknown>)) return '1.0';
  if (hasV1RootPropFlags(legacyProps)) return '1.0';
  if (hasV1OptionsFlags(external?.options)) return '1.0';

  // V2 structural markers: metadata, sharedControls, or empty columns with no V1 signals
  if (external?.metadata != null || external?.sharedControls != null) return '2.0';

  return '1.0';
}

function inferColumnType(c: Record<string, unknown>): ColumnType | undefined {
  const skipBadge = c.noBadge === true;

  const tryBadge = (): ColumnType | undefined => {
    if (skipBadge) return undefined;
    if (c.isInjectedBadgeField === true || c.isDefaultBadgeField === true || c.isBadgeField === true) {
      return 'badge';
    }
    return undefined;
  };

  const chain: Array<[string, ColumnType]> = [
    ['isHtmlFormattedField', 'html'],
    ['isModalField', 'modal'],
    ['isCoupleDateRangeField', 'coupleWithDateRange'],
    ['isCoupleField', 'couple'],
    ['isLinkField', 'link'],
    ['isDateRangeField', 'dateRange'],
    ['isConditionalDateField', 'conditionalDate'],
    ['isDateFieldWithTimeLTS', 'dateTimeLTS'],
    ['isDateFieldWithTime', 'dateTime'],
    ['isDateField', 'date'],
    ['isCurrencyField', 'currency'],
    ['isPercentageField', 'percentage'],
    ['isNumberField', 'number'],
    ['isConditionalField', 'conditional'],
    ['isMaskBehaviorField', 'mask'],
    ['isTimeField', 'time'],
    ['isArraySpliterField', 'arraySplit'],
    ['isSpaceField', 'space'],
  ];

  const b = tryBadge();
  if (b) return b;

  for (const [k, t] of chain) {
    if (c[k] === true) return t;
  }
  return undefined;
}

function mergeFormat(
  existing: FormatConfig | undefined,
  patch: FormatConfig,
): FormatConfig {
  return { ...existing, ...patch };
}

function mapAggregate(v: unknown): AggregateDef | string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'string') {
    const fn = v as AggregateDef['fn'];
    const allowed: AggregateDef['fn'][] = ['sum', 'average', 'count', 'min', 'max'];
    if (allowed.includes(fn)) return { fn };
    return { fn: 'sum' };
  }
  if (typeof v === 'object' && v !== null && 'fn' in (v as object)) {
    return v as AggregateDef;
  }
  return undefined;
}

/**
 * Map a single V1 column definition to V2 `ExternalColumnDef`.
 */
function adaptV1Column(
  v1: Record<string, unknown>,
  injectionByField?: Map<string, DataSourceDef>,
): ExternalColumnDef {
  const accessor = String(v1.accessor ?? v1.id ?? '');
  const field = accessor;
  const col: ExternalColumnDef = {
    field,
    header: String(v1.Header ?? v1.header ?? field),
  };

  if (typeof v1.width === 'number') col.width = v1.width;

  const inferred = inferColumnType(v1);
  if (inferred) col.type = inferred;

  if (v1.center === true || v1.cellCenter === true) col.align = 'center';
  if (v1.toUpperCase === true) col.uppercase = true;
  if (v1.ifEmptyText != null) col.emptyDisplay = String(v1.ifEmptyText);
  if (v1.showBlankWhenNull === true) col.emptyDisplay = '';
  if (v1.conditionalValue != null && typeof v1.conditionalValue === 'object') {
    col.conditionalValues = v1.conditionalValue as Record<string, string>;
  }
  if (Array.isArray(v1.clearFields)) {
    col.clearOnEdit = v1.clearFields.map(String);
  }
  if (v1.sortable === true) col.sortable = true;
  if (v1.prefix != null) col.prefix = String(v1.prefix);
  if (v1.fixed === 'left' || v1.fixed === 'right') col.fixed = v1.fixed;

  if (v1.fields != null && typeof v1.fields === 'object') {
    col.fields = v1.fields as Record<string, string>;
  }

  if (v1.isSubHeader === true) col.visibility = 'picker';
  // isSubHeader: false → omit visibility (normalizer defaults to 'locked')

  if (v1.heading != null) col.group = String(v1.heading);

  let format: FormatConfig | undefined;
  if (v1.showDecimal === true) {
    format = mergeFormat(format, { decimals: 2, fixed: true });
  }
  if (v1.formatByComma === true) {
    format = mergeFormat(format, { commas: true });
  }
  if (v1.toFixed != null && typeof v1.toFixed === 'number') {
    format = mergeFormat(format, { decimals: v1.toFixed, fixed: true });
  }
  if (format) col.format = format;

  const agg = mapAggregate(v1.aggregate);
  if (agg !== undefined) col.aggregate = agg;

  // Badge config (noBadge already stripped type to text via inferColumnType)
  if (col.type === 'badge' && v1.noBadge !== true) {
    const badge: BadgeConfig = {
      statusMap: (v1.badgeStatus as BadgeConfig['statusMap']) ?? {},
    };
    if (v1.defaultBadgeStatus != null) {
      badge.defaultVariant = v1.defaultBadgeStatus as BadgeConfig['defaultVariant'];
    }
    if (v1.isInjectedBadgeField === true) badge.injected = true;
    if (v1.isDefaultBadgeField === true) {
      badge.defaultVariant = badge.defaultVariant ?? 'default';
    }
    col.badge = badge;
  }

  if (v1.popoverInfo != null && typeof v1.popoverInfo === 'object') {
    col.modal = { ...(col.modal ?? {}), popoverInfo: v1.popoverInfo as Record<string, unknown> };
  }

  if (col.type === 'link' && v1.url != null) {
    col.link = { ...(col.link ?? {}), url: String(v1.url) };
  }

  if (v1.maskBehavior != null && typeof v1.maskBehavior === 'object') {
    const mb = v1.maskBehavior as { format?: 'partial' | 'full'; valueField?: string };
    col.mask = {
      format: mb.format ?? 'partial',
      valueField: mb.valueField ?? field,
    };
  }

  if (col.type === 'dateRange' && v1.fields != null) {
    const f = v1.fields as Record<string, string>;
    col.dateRange = {
      startField: f.start ?? f.startField ?? '',
      endField: f.end ?? f.endField ?? '',
    };
  }

  if (col.type === 'couple' || col.type === 'coupleWithDateRange') {
    const f = v1.fields as Record<string, string> | undefined;
    col.couple = {
      titleField: f?.titleField,
      subtitleField: f?.subtitleField ?? f?.subtitle ?? accessor,
      ...col.couple,
    };
    if (col.type === 'coupleWithDateRange' && f != null && !col.dateRange) {
      col.dateRange = {
        startField: f.start ?? f.startField ?? '',
        endField: f.end ?? f.endField ?? '',
      };
    }
  }

  if (v1.enableTag === true) {
    if (col.type === 'modal') {
      col.modal = { ...(col.modal ?? {}), enableTag: true };
    } else if (col.type === 'couple' || col.type === 'coupleWithDateRange') {
      col.couple = { ...col.couple!, showTag: true };
    }
  }

  if (v1.editable === true) {
    const ed: EditableDef = {
      type: mapEditableType(v1.editableType as string | undefined),
    };
    if (v1.validation != null) {
      ed.validation = mapColumnValidation(v1.validation as Record<string, unknown>);
    }
    if (v1.defaultValueName != null) {
      ed.defaultValueField = String(v1.defaultValueName);
    }
    col.editable = ed;
  } else if (v1.editable && typeof v1.editable === 'object') {
    col.editable = v1.editable as EditableDef;
  }

  const hasRender =
    typeof v1.Cell === 'function' ||
    typeof v1.renderCell === 'function' ||
    typeof v1.cell === 'function';
  if (hasRender) {
    col.renderer = 'v1-cell';
  }

  const inj = injectionByField?.get(accessor);
  if (inj) col.dataSource = inj;

  return col;
}

function mapComputeRules(rules: unknown): ComputeRule[] | undefined {
  if (!Array.isArray(rules) || rules.length === 0) return undefined;
  const out: ComputeRule[] = [];
  for (const r of rules) {
    const raw = r as Record<string, unknown>;
    const op = String(raw.operator ?? 'add').toLowerCase() as ComputeRule['operator'];
    const operator = COMPUTE_OPS.has(op) ? op : 'add';
    if (!Array.isArray(raw.sourceFields) || raw.targetField == null) continue;
    out.push({
      sourceFields: raw.sourceFields.map(String),
      operator,
      targetField: String(raw.targetField),
      precision: typeof raw.precision === 'number' ? raw.precision : undefined,
    });
  }
  return out.length ? out : undefined;
}

function buildFeatures(
  opts: Record<string, unknown> | undefined,
  v1Props: Partial<LegacyDynamicTableProps> | undefined,
  subRows: SubRowConfig | undefined,
): FeaturesConfig | undefined {
  const showPagination = v1Props?.showPagination ?? opts?.showPagination;
  const showSearch = !!(opts?.showSearch === true || opts?.enableSearch === true);
  const showColumn = v1Props?.showColumn ?? opts?.showColumn;
  const groupFields = (v1Props?.groupBy ?? opts?.groupBy) as string[] | undefined;
  const subRowKey = v1Props?.subRowKey ?? opts?.subRowKey;
  const showCheckBox = v1Props?.showCheckBox === true;
  const filteredKey = v1Props?.filteredKey;
  const showSelectAllButton = v1Props?.showSelectAllButton === true;

  const features: FeaturesConfig = {};

  if (showPagination === true) {
    features.pagination = { mode: 'pagination' };
  } else if (v1Props?.onLoadMore) {
    features.pagination = { mode: 'loadMore', loadMore: { trigger: 'button' } };
  }

  const pageSize = opts?.paginationCount;
  if (typeof pageSize === 'number' && features.pagination) {
    features.pagination.defaultSize = pageSize;
  } else if (typeof pageSize === 'number') {
    features.pagination = { defaultSize: pageSize };
  }

  if (showSearch) {
    features.search = { enabled: true };
  }

  if (showColumn === true) {
    features.columnManagement = {
      ...(features.columnManagement ?? {}),
      selectorEnabled: true,
    };
  }

  if (Array.isArray(groupFields) && groupFields.length > 0) {
    features.groupBy = { fields: [...groupFields] };
  }

  if (showCheckBox) {
    features.selection = {
      keyField: filteredKey != null ? String(filteredKey) : 'id',
    };
  }

  const rows: FeaturesConfig['rows'] = {};
  if (v1Props?.coloredTable === true) rows.striped = true;
  if (typeof v1Props?.scrollableHeight === 'number') rows.maxHeight = v1Props.scrollableHeight;
  if (subRows) rows.subRows = subRows;
  else if (subRowKey) {
    rows.subRows = {
      dataField: String(subRowKey),
      columns: [],
    };
  }
  if (rows.striped != null || rows.maxHeight != null || rows.subRows != null) {
    features.rows = rows;
  }

  if (opts?.showImportBlock === true) {
    features.import = {
      enabled: true,
      ...(opts.importDescription != null
        ? { description: String(opts.importDescription) }
        : {}),
    };
  }

  return Object.keys(features).length > 0 ? features : undefined;
}

function buildTableDataSource(
  opts: Record<string, unknown> | undefined,
  v1Schema: Record<string, unknown>,
): DataSourceConfig | undefined {
  const filtersRaw = opts?.filters;
  if (!Array.isArray(filtersRaw) || filtersRaw.length === 0) return undefined;
  const endpoint =
    typeof v1Schema.endpoint === 'string' && v1Schema.endpoint.length > 0
      ? v1Schema.endpoint
      : '';
  const rowIdField =
    typeof v1Schema.rowIdField === 'string' && v1Schema.rowIdField.length > 0
      ? v1Schema.rowIdField
      : 'id';
  return {
    endpoint,
    rowIdField,
    method: 'GET',
    filters: filtersRaw.map((f) => mapV1FilterToV2(f as Record<string, unknown>)),
  };
}

/**
 * Convert a full V1 schema + optional root props into V2 `ExternalTableSchema`.
 * Props override schema.options for overlapping feature flags.
 */
export function adaptLegacySchema(
  v1Schema: any,
  v1Props?: Partial<LegacyDynamicTableProps>,
): ExternalTableSchema {
  const schemaObj = v1Schema as Record<string, unknown>;
  const colsRaw = schemaObj.columns;
  const columnsIn = Array.isArray(colsRaw) ? colsRaw : [];

  const injectionByField = buildInjectionMap(
    schemaObj.apiInjection as LegacyDynamicTableProps['schema']['apiInjection'],
  );
  const subInjectionByField = buildInjectionMap(
    schemaObj.subRowApiInjection as LegacyDynamicTableProps['schema']['apiInjection'],
  );

  const columns: ExternalColumnDef[] = columnsIn.map((c) =>
    adaptV1Column(c as Record<string, unknown>, injectionByField),
  );

  const subColsRaw = (v1Props?.schema?.subRowColumns ?? schemaObj.subRowColumns) as
    | any[]
    | undefined;
  let subRows: SubRowConfig | undefined;
  if (Array.isArray(subColsRaw) && subColsRaw.length > 0) {
    const subRowKey =
      v1Props?.subRowKey ??
      (schemaObj.options as Record<string, unknown> | undefined)?.subRowKey ??
      (typeof schemaObj.subRowKey === 'string' ? schemaObj.subRowKey : '');
    subRows = {
      dataField: String(subRowKey || 'subRows'),
      columns: subColsRaw.map((c) =>
        adaptV1Column(c as Record<string, unknown>, subInjectionByField),
      ),
    };
  }

  const opts = (schemaObj.options ?? v1Props?.schema?.options) as
    | Record<string, unknown>
    | undefined;

  const features = buildFeatures(opts, v1Props, subRows);
  const computeRules = mapComputeRules(schemaObj.computeRules);
  const dataSource = buildTableDataSource(opts, schemaObj);

  const result: ExternalTableSchema = { columns };
  if (features) result.features = features;
  if (computeRules) result.computeRules = computeRules;
  if (dataSource) result.dataSource = dataSource;

  return result;
}

const CALLBACK_KEYS = [
  'onChecked',
  'onSave',
  'handleExportBulk',
  'handleSendInvite',
  'handleColumnSave',
  'handleTableColumn',
  'handleEarnMultiplier',
  'handlePublishAward',
  'handleSendEmail',
  'onLoadMore',
  'onFilterClick',
  'onCheckboxEditDataChange',
  'setIsHovering',
  'setSelectedTableData',
  'setCheckedItems',
  'setInviteStatus',
  'inivitationBlockDatas',
  'components',
] as const;

/**
 * Pull V1 callback/slot props into a typed object for the V2 table facade.
 */
export function mapLegacyPropsToV2Callbacks(props: any): V2CallbackProps {
  if (!props || typeof props !== 'object') return {};
  const out: V2CallbackProps = {};
  for (const k of CALLBACK_KEYS) {
    if (props[k] !== undefined) {
      (out as Record<string, unknown>)[k] = props[k];
    }
  }
  return out;
}
